import tenant from '../config/tenant';
import { getAuthKeyFromLocalStorage } from '../store/slices/employeeSlice';

async function overtimeFetch(url, token, method = 'GET', body = null) {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['auth-key'] = token;
  }

  const options = {
    method,
    headers,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    const res = await fetch(url, options);

    if (res.status === 404) {
      return { __noData: true };
    }

    if (res.status === 500) {
      console.warn('[overtimeFetch] Server returned 500 (likely no records):', url);
      return { __noData: true };
    }

    if (!res.ok) {
      const text = await res.text().catch(() => `HTTP ${res.status}`);
      throw new Error(text || `HTTP error ${res.status}`);
    }

    return await res.json();
  } catch (error) {
    if (error.message && error.message.includes('HTTP')) {
      throw error;
    }
    console.warn('[overtimeFetch] Error fetching, treating as no data:', error);
    return { __noData: true };
  }
}



/**
 * Normalise an attendance day/month
 */
function normaliseAttDay(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const full = raw.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?/);
  if (full) {
    const [, y, m] = full;
    return `${y}-${m.padStart(2, '0')}`;
  }
  return raw;
}

/**
 * Get OT details with approval filter
 * @param {Object} params
 * @param {string} params.employeeId
 * @param {string} params.attDay
 * @param {number} params.isApproved
 * @returns {Promise<Array>}
 */
export async function getOTDetails({ employeeId, attDay, isApproved } = {}) {
  const token = getAuthKeyFromLocalStorage();
  const params = new URLSearchParams();

  if (employeeId && String(employeeId).trim()) {
    params.append('p_employeeId', String(employeeId).trim());
  }

  const month = normaliseAttDay(attDay);
  if (month) {
    params.append('p_attday', month);
  }

  if (isApproved !== undefined && isApproved !== null) {
    params.append('p_isApproved', String(isApproved));
  }

  const query = params.toString();

  if (!query) {
    return [];
  }

  const url = `${tenant.api.baseUrl}/OT/GetOTDetails${query ? `?${query}` : ''}`;

  try {
    const data = await overtimeFetch(url, token);

    if (data && data.__noData) return [];

    if (data && data.ResultSet && Array.isArray(data.ResultSet) && data.ResultSet.length === 0) {
      return [];
    }

    if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
      if (
        data.StatusCode === 404 ||
        data.StatusCode === 500 ||
        /no\s*(data|record)/i.test(data.Message || '')
      ) {
        return [];
      }
      throw new Error(data.Message || `Server error: status ${data.StatusCode}`);
    }

    let resultSet = null;

    if (Array.isArray(data)) {
      resultSet = data;
    } else if (data) {
      resultSet = data.ResultSet ?? data.Result ?? data.Data ?? data.rows ?? data.items;

      if (resultSet && typeof resultSet === 'object' && !Array.isArray(resultSet)) {
        if (resultSet.HOT_OVERTIME_ID) {
          resultSet = [resultSet];
        } else {
          const arrayProps = Object.keys(resultSet).filter((key) =>
            Array.isArray(resultSet[key])
          );
          if (arrayProps.length > 0) {
            resultSet = resultSet[arrayProps[0]];
          }
        }
      }
    }

    return Array.isArray(resultSet) ? resultSet : [];
  } catch (error) {
    console.error('[getOTDetails] Error:', error);
    return [];
  }
}


/**
 * Approve an OT record (single)
 * @param {Object} params
 * @param {string} params.employeeId - Employee ID
 * @param {string} params.overtimeId - OT ID to approve
 * @param {string} params.approvedBy - Approver's user ID
 * @returns {Promise<Object>} Approval response
 */
export async function approveOT({ employeeId, overtimeId, approvedBy }) {
  const token = getAuthKeyFromLocalStorage();
  const params = new URLSearchParams();

  if (employeeId) params.append('p_employeeId', employeeId);
  if (overtimeId) params.append('p_overtimeId', overtimeId);
  if (approvedBy) params.append('p_approvedBy', approvedBy);

  const query = params.toString();
  const url = `${tenant.api.baseUrl}/OT/OTApprovals${query ? `?${query}` : ''}`;
  
  const data = await overtimeFetch(url, token, 'POST');

  if (data && data.__noData) {
    return { success: true, message: 'OT approved successfully' };
  }

  if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
    throw new Error(data.Message || `Approval failed: status ${data.StatusCode}`);
  }

  return data || { success: true };
}

/**
 * Bulk approve OT records
 * @param {Object} params
 * @param {Array} params.overtimeIds - Array of OT IDs to approve
 * @param {string} params.approvedBy - Approver's user ID
 * @returns {Promise<Object>} Approval response
 */
export async function bulkApproveOT({ records, approvedBy }) {
  const token = getAuthKeyFromLocalStorage();

  const results = [];
  const errors = [];

  for (const { employeeId, overtimeId } of records) {
    try {
      const params = new URLSearchParams();
      if (employeeId) params.append('p_employeeId', employeeId);
      params.append('p_overtimeId', overtimeId);
      params.append('p_approvedBy', approvedBy);

      const url = `${tenant.api.baseUrl}/OT/OTApprovals?${params.toString()}`;
      const data = await overtimeFetch(url, token, 'POST');

      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        errors.push({ overtimeId, error: data.Message || 'Approval failed' });
      } else {
        results.push({ overtimeId, success: true });
      }
    } catch (error) {
      errors.push({ overtimeId, error: error.message });
    }
  }

  return {
    success: errors.length === 0,
    results,
    errors,
    total: records.length,
    succeeded: results.length,
    failed: errors.length,
  };
}

/**
 * Get both pending and approved OT details
 * @param {Object} params
 * @param {string} params.employeeId - Employee ID (optional)
 * @param {string} params.attDay - Attendance date (optional)
 * @returns {Promise<{pending: Array, approved: Array, all: Array}>}
 */
export async function getOTDetailsWithStatus({ employeeId, attDay } = {}) {
  const [pending, approved] = await Promise.all([
    getOTDetails({ employeeId, attDay, isApproved: 0 }),
    getOTDetails({ employeeId, attDay, isApproved: 1 }),
  ]);

  return {
    pending: pending || [],
    approved: approved || [],
    all: [...(pending || []), ...(approved || [])],
  };
}

export async function getOTRateHistory(sno) {
  const token = getAuthKeyFromLocalStorage();
  const url = `${tenant.api.baseUrl}/OT/GetOTHistoryBySno?p_sno=${encodeURIComponent(sno)}`;

  try {
    const data = await overtimeFetch(url, token);

    if (data && data.__noData) return [];
    if (data && data.StatusCode !== 200) {
      throw new Error(data.Message || `Server error: status ${data.StatusCode}`);
    }

    return data?.ResultSet || [];
  } catch (error) {
    console.error('[getOTRateHistory] Error: ', error);
    return [];
  }
}